/**
 * TextEditor - Rich text editor component using TipTap
 * 
 * Migrated from Jodit Pro to TipTap.
 * Maintains backward compatibility with the original API.
 * 
 * Features:
 * - Rich text formatting (bold, italic, lists, tables, etc.)
 * - Placeholder autocomplete triggered by $ character
 * - Placeholder chip rendering with color coding
 * - Custom image upload to file service
 * - Image paste handling
 * - Table support with formatting
 */

import React, { Component, createRef } from 'react';
import Grid from '@mui/material/Grid';
import { makeStyles, createStyles } from '@mui/styles';
import { withStyles } from '@mui/styles';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { Tooltip } from '@mui/material';
import { TipTapEditor } from '../TipTap';

const componentStyles = makeStyles(() => {
  createStyles({
    root: {
      flexGrow: 1,
    },
  });
});

const useStyles = () => ({
  gridJodit: {},
  gridJoditTier: {},
});

/**
 * Normalizes table HTML for consistent rendering
 * @param {string} content - HTML content
 * @returns {string} - Normalized HTML
 */
const normalizeTableHtml = (content) => {
  if (!content) return '';
  
  let normalized = content;
  
  if (normalized.includes('table')) {
    normalized = normalized
      .replace(/(<table style=")/g, () => {
        return `<table border="1" style="border-collapse:collapse; `;
      })
      .replace(/(border-color:)/g, 'border: 1px solid');
  }
  
  normalized = normalized
    .replace(/(?:\r\n|\r|\n)/g, '<br>')
    .replace(/(<td style="width: [0-9.]+%;">)/g, (val) => {
      const width = val.match(/([0-9.]+)/g)[0];
      return `<td style="width: ${width}%; padding: 4px;">`;
    });
  
  return normalized;
};

class TextEditor extends Component {
  editorRef = createRef();
  
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      content: normalizeTableHtml(this.props.html || ''),
    };
    
    // Call editorFocus on mount if provided
    if (this.props.editorFocus) {
      this.props.editorFocus();
    }
  }

  componentDidMount() {
    this.updateContentFromHtml();
  }

  componentDidUpdate(prevProps) {
    // Update content if html prop changes
    if (this.props.html !== prevProps.html) {
      this.updateContentFromHtml();
    }

    // Handle placeholder insertion from external source
    const { placeholder } = this.props;
    if (placeholder && prevProps.placeholder && 
        placeholder.length !== prevProps.placeholder.length) {
      this.insertPlaceholder(placeholder[placeholder.length - 1]);
    }
  }

  updateContentFromHtml() {
    const content = normalizeTableHtml(this.props.html || '');
    
    if (this.state.content !== content) {
      this.setState({ content });
    }
  }

  onFocus = () => {
    if (this.props.editorFocus) {
      this.props.editorFocus();
    }
  };

  handleBlur = (value) => {
    // Normalize table HTML before passing to parent
    const normalized = normalizeTableHtml(value);
    
    if (this.props.onChange) {
      this.props.onChange(normalized);
    }
  };

  handleChange = (value) => {
    // Update internal state
    this.setState({ content: value });
  };

  /**
   * Insert a placeholder at the current cursor position
   * Maintains backward compatibility with the original Jodit API
   */
  insertPlaceholder = (placeholder) => {
    if (placeholder && this.editorRef.current) {
      this.editorRef.current.insertPlaceholder(placeholder);
    }
  };

  render() {
    const { content } = this.state;
    const { classes, disableTooltip = false, availablePlaceholders } = this.props;
    
    if (this.state.loading) {
      return (
        <div className="my-nice-tab-container">
          <div className="loading-state"></div>
        </div>
      );
    }

    return (
      <Tooltip
        placement="bottom"
        title={disableTooltip !== true ? 'Type "$" to view and insert placeholders' : ''}
        enterDelay={1200}
        enterNextDelay={1200}
        disableHoverListener={disableTooltip}
        disableFocusListener={disableTooltip}
        disableTouchListener={disableTooltip}
      >
        <Grid 
          container 
          onClick={this.onFocus} 
          className={componentStyles.root} 
          spacing={3}
        >
          <Grid
            item
            className={
              useProjectStore.getState().tier === 'FREE' ? classes.gridJodit : classes.gridJoditTier
            }
            xs={12}
          >
            <TipTapEditor
              ref={this.editorRef}
              value={content}
              onChange={this.handleChange}
              onBlur={this.handleBlur}
              onFocus={this.onFocus}
              availablePlaceholders={availablePlaceholders}
              projectId={useProjectStore.getState().project_id}
              showToolbar={true}
              triggerChars={['$']}
              placeholder=""
            />
          </Grid>
        </Grid>
      </Tooltip>
    );
  }
}

export default withStyles(useStyles)(TextEditor);
